import { env } from '@/config/env'
import { TIME } from '@/constants/time'
import { useRealtimeStore } from '@/store/realtime.store'
import type { AlarmEvent, WsClientMessage, WsServerMessage } from '@/types/realtime'

type AlarmHandler = (event: AlarmEvent) => void

/**
 * Singleton realtime client. One WebSocket per authenticated session; all tag
 * subscriptions are multiplexed over it with reference counting so a widget
 * mounting/unmounting doesn't churn server subscriptions.
 *
 * Lives outside React so it survives re-renders and StrictMode double-mounts.
 * Tag values are written straight into the (vanilla) realtime store; alarm
 * events are forwarded to a registered handler (wired by useWebSocket to RTK
 * cache invalidation + toasts).
 *
 * When no server is reachable it simply retries with capped backoff while the
 * rest of the app falls back to REST polling — no crashes, no data loss.
 */
class RealtimeClient {
  private ws: WebSocket | null = null
  private token: string | null = null
  private intentionalClose = false
  private reconnectAttempts = 0
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  /** tagId → active subscriber count. */
  private readonly refCounts = new Map<string, number>()
  private alarmHandler: AlarmHandler | null = null

  setAlarmHandler(handler: AlarmHandler | null): void {
    this.alarmHandler = handler
  }

  /** Open the connection for a session. Idempotent for the same token. */
  connect(token: string): void {
    if (this.ws && this.token === token) return
    this.token = token
    this.intentionalClose = false
    this.open()
  }

  /** Tear down the connection and clear all realtime state (on logout). */
  disconnect(): void {
    this.intentionalClose = true
    this.token = null
    this.clearReconnect()
    this.refCounts.clear()
    if (this.ws) {
      this.ws.onclose = null // suppress reconnect from our own close
      this.ws.close()
      this.ws = null
    }
    useRealtimeStore.getState().clearAll()
  }

  subscribeToTag(tagId: string): void {
    const next = (this.refCounts.get(tagId) ?? 0) + 1
    this.refCounts.set(tagId, next)
    if (next === 1) {
      useRealtimeStore.getState().addSubscription(tagId)
      this.send({ type: 'subscribe', tagIds: [tagId] })
    }
  }

  unsubscribeFromTag(tagId: string): void {
    const current = this.refCounts.get(tagId) ?? 0
    if (current <= 1) {
      this.refCounts.delete(tagId)
      useRealtimeStore.getState().removeSubscription(tagId)
      this.send({ type: 'unsubscribe', tagIds: [tagId] })
    } else {
      this.refCounts.set(tagId, current - 1)
    }
  }

  // ─── internals ─────────────────────────────────────────────────────────────

  private open(): void {
    useRealtimeStore.getState().setWsStatus('connecting')
    let socket: WebSocket
    try {
      socket = new WebSocket(env.VITE_WS_URL)
    } catch {
      this.scheduleReconnect()
      return
    }
    this.ws = socket

    socket.onopen = () => {
      this.reconnectAttempts = 0
      useRealtimeStore.getState().setWsStatus('connected')
      if (this.token) this.send({ type: 'auth', token: this.token })
      // Alarm events are auto-broadcast to every authenticated client in the
      // tenant — no explicit alarm subscription needed.
      // Re-subscribe to everything still referenced after a reconnect (batched).
      const tagIds = [...this.refCounts.keys()]
      if (tagIds.length > 0) this.send({ type: 'subscribe', tagIds })
    }

    socket.onmessage = (event: MessageEvent<string>) => {
      this.handleMessage(event.data)
    }

    socket.onerror = () => {
      useRealtimeStore.getState().setWsStatus('error')
    }

    socket.onclose = () => {
      this.ws = null
      if (this.intentionalClose) return
      useRealtimeStore.getState().setWsStatus('disconnected')
      this.scheduleReconnect()
    }
  }

  private handleMessage(raw: string): void {
    let message: WsServerMessage
    try {
      message = JSON.parse(raw) as WsServerMessage
    } catch {
      return
    }
    if (message.type === 'tag_value') {
      // tap-realtime sends tag values flat: { type, tagId, value, quality, timestamp }
      useRealtimeStore.getState().setTagValue(message.tagId, {
        tagId: message.tagId,
        value: message.value,
        quality: message.quality,
        timestamp: message.timestamp,
      })
    } else if (message.type === 'alarm_event') {
      this.alarmHandler?.(message.alarm)
    }
  }

  private send(message: WsClientMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message))
    }
  }

  private scheduleReconnect(): void {
    if (this.intentionalClose || !this.token) return
    this.clearReconnect()
    // Exponential backoff: 1s, 2s, 4s, 8s … capped at WS_RECONNECT_MAX_MS.
    const delay = Math.min(
      TIME.WS_RECONNECT_BASE_MS * 2 ** this.reconnectAttempts,
      TIME.WS_RECONNECT_MAX_MS,
    )
    this.reconnectAttempts += 1
    this.reconnectTimer = setTimeout(() => this.open(), delay)
  }

  private clearReconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
  }
}

/** App-wide singleton. */
export const realtimeClient = new RealtimeClient()
